import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Loader2, CheckCircle2 } from "lucide-react";
import { track, ids } from "../analytics/sdk";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MARKETS = ["Statewide", "Tampa Bay", "South Florida", "Northeast Florida", "Orlando / Central Florida", "Treasure Coast / Palm Beaches"];
const INTERESTS = ["Presenting Sponsor", "Regional Sponsor", "Add-ons only", "Not sure yet"];
const EMPTY = { name: "", email: "", company: "", phone: "", website_url: "", markets: [], sponsorship_interest: "", event_details: "" };
const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`);
const PHONE_RE = /^\+?1?[\s.-]?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;

const validate = (f) => {
  const e = {};
  if (!f.name.trim()) e.name = "Please enter your name.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) e.email = "Please enter a valid email address.";
  if (!f.company.trim()) e.company = "Please enter your company.";
  if (!PHONE_RE.test(f.phone.trim())) e.phone = "Please enter a valid phone number.";
  if (!f.sponsorship_interest) e.sponsorship_interest = "Pick the option closest to your interest.";
  if (!f.event_details.trim()) e.event_details = "Tell us a little about your goals.";
  return e;
};

const Field = ({ label, id, error, children, optional }) => (
  <div>
    <label htmlFor={id} className="font-gothic mb-2 block uppercase tracking-wider text-xs text-black/70">{label} {optional && <span className="text-black/40 normal-case tracking-normal">(optional)</span>}</label>
    {children}
    {error && <p data-testid={`error-${id}`} role="alert" className="mt-1.5 text-xs text-red-600">{error}</p>}
  </div>
);

export const SponsorshipForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [serverError, setServerError] = useState("");
  const [honey, setHoney] = useState("");
  const token = useRef(null);
  const inFlight = useRef(false);
  useEffect(() => { if (status === "success") window.scrollTo({ top: document.getElementById("become-a-sponsor")?.offsetTop - 120 || 0, behavior: "smooth" }); }, [status]);

  const onStart = () => { if (token.current) return; token.current = { key: uuid(), started_at: Date.now() }; track("form_start", { name: "sponsorship" }); };
  const set = (k) => (e) => { onStart(); setForm((f) => ({ ...f, [k]: e.target.value })); if (errors[k]) setErrors((er) => ({ ...er, [k]: undefined })); };
  const toggleMarket = (m) => { onStart(); setForm((f) => ({ ...f, markets: f.markets.includes(m) ? f.markets.filter((x) => x !== m) : [...f.markets, m] })); };
  const cls = (k) => `form-input ${errors[k] ? "has-error" : ""}`;
  const post = (body, retry) => axios.post(`${API}/inquiries`, body).catch((err) => { if (retry && !err.response) return post(body, false); throw err; });

  const submit = async (e) => {
    e.preventDefault();
    if (inFlight.current) return;
    const errs = validate(form); setErrors(errs);
    if (Object.keys(errs).length) { track("form_error", { name: "sponsorship", fields: Object.keys(errs) }); return; }
    onStart(); inFlight.current = true; setStatus("loading"); setServerError("");
    try {
      const { data } = await post({
        form_name: "Sponsorship inquiry", name: form.name.trim(), email: form.email.trim(), company: form.company.trim(), phone: form.phone.trim(), website_url: form.website_url.trim() || null,
        markets: form.markets, sponsorship_interest: form.sponsorship_interest, event_type: "Sponsorship", event_details: form.event_details.trim(),
        idempotency_key: token.current.key, form_started_at: token.current.started_at, website: honey, submitted_from_path: window.location.pathname, ...ids(),
      }, true);
      if (!data.duplicate) track("form_submit_success", { name: "sponsorship", inquiry_id: data?.id });
      setStatus("success");
    } catch (err) {
      setStatus("idle");
      const d = err?.response?.data?.detail;
      if (Array.isArray(d)) { const fe = {}; d.forEach((x) => { const k = x?.loc?.[x.loc.length - 1]; if (k) fe[k] = x.msg?.replace(/^Value error, /, ""); }); setErrors(fe); }
      else setServerError(typeof d === "string" ? d : "Something went wrong. Please try again or email ally@trivianation.com.");
    } finally { inFlight.current = false; }
  };

  if (status === "success") return (
    <div data-testid="sponsor-success" className="rounded-2xl bg-en-teal p-10 text-center text-white">
      <CheckCircle2 className="mx-auto h-12 w-12 text-en-yellow" />
      <h3 className="font-display mt-5 text-3xl">Thanks! We'll be in touch shortly.</h3>
      <p className="mt-3 text-white">Our sponsorship team will reach out to schedule a call.</p>
    </div>
  );

  return (
    <form data-testid="sponsor-form" onSubmit={submit} noValidate className="grid gap-5 sm:grid-cols-2">
      <Field label="Name" id="name" error={errors.name}><input id="name" data-testid="sponsor-name" className={cls("name")} value={form.name} onChange={set("name")} autoComplete="name" /></Field>
      <Field label="Email" id="email" error={errors.email}><input id="email" type="email" data-testid="sponsor-email" className={cls("email")} value={form.email} onChange={set("email")} autoComplete="email" /></Field>
      <Field label="Company" id="company" error={errors.company}><input id="company" data-testid="sponsor-company" className={cls("company")} value={form.company} onChange={set("company")} autoComplete="organization" /></Field>
      <Field label="Phone" id="phone" error={errors.phone}><input id="phone" type="tel" required data-testid="sponsor-phone" className={cls("phone")} value={form.phone} onChange={set("phone")} autoComplete="tel" /></Field>
      <Field label="Website" id="website_url" optional><input id="website_url" data-testid="sponsor-website" className={cls("website_url")} value={form.website_url} onChange={set("website_url")} placeholder="https://" /></Field>
      <Field label="Sponsorship interest" id="sponsorship_interest" error={errors.sponsorship_interest}>
        <select id="sponsorship_interest" data-testid="sponsor-interest" className={cls("sponsorship_interest")} value={form.sponsorship_interest} onChange={set("sponsorship_interest")}>
          <option value="">Select one</option>{INTERESTS.map((i) => <option key={i}>{i}</option>)}
        </select>
      </Field>
      <div className="sm:col-span-2">
        <p className="font-gothic mb-2 uppercase tracking-wider text-xs text-black/70">Markets of interest</p>
        <div className="flex flex-wrap gap-2">
          {MARKETS.map((m) => (
            <button type="button" key={m} data-testid={`market-${m.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => toggleMarket(m)} aria-pressed={form.markets.includes(m)} className={`rounded-full border px-4 py-2 text-xs font-medium transition-colors ${form.markets.includes(m) ? "border-en-teal bg-en-teal text-white" : "border-black/15 bg-white text-black/70 hover:border-en-teal"}`}>{m}</button>
          ))}
        </div>
      </div>
      <div className="sm:col-span-2"><Field label="Goals & details" id="event_details" error={errors.event_details}><textarea id="event_details" rows={5} data-testid="sponsor-details" className={cls("event_details")} value={form.event_details} onChange={set("event_details")} placeholder="What are you hoping to achieve? Markets, timing, audience — anything helps." /></Field></div>
      <div aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 overflow-hidden"><label htmlFor="website">Website</label><input id="website" name="website" tabIndex={-1} autoComplete="off" value={honey} onChange={(e) => setHoney(e.target.value)} /></div>
      {serverError && <p data-testid="sponsor-server-error" role="alert" className="sm:col-span-2 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-700">{serverError}</p>}
      <div className="sm:col-span-2 flex justify-end"><button type="submit" data-testid="sponsor-submit" disabled={status === "loading"} className="btn-primary !px-8 !py-4 disabled:opacity-60">{status === "loading" ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : "Become a Sponsor"}</button></div>
    </form>
  );
};
